import { cn } from '@/lib/shadcn/utils';
import Link from 'next/link';
import React, { type FC } from 'react';

type ActionItem = {
  readonly text: string;
  readonly href: string;
};

type CaseStudyActionsProps = React.HTMLAttributes<HTMLDivElement> & {
  readonly actions: ActionItem[];
};

const CaseStudyHeader: FC<Readonly<React.HTMLAttributes<HTMLDivElement>>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
};

const CaseStudy: FC<Readonly<React.HTMLAttributes<HTMLDivElement>>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'lg:flex lg:justify-between lg:items-center py-16',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const CaseStudyColumn: FC<Readonly<React.HTMLAttributes<HTMLDivElement>>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={cn('lg:pr-8', className)} {...props}>
      <div className="pb-16 lg:max-w-lg">{children}</div>
    </div>
  );
};

const CaseStudyHeading: FC<Readonly<React.HTMLAttributes<HTMLDivElement>>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <h2
      className={cn('text-base/7 font-semibold text-[#F24E1E]', className)}
      {...props}
    >
      {children}
    </h2>
  );
};

const CaseStudySubHeading: FC<
  Readonly<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, ...props }) => {
  return (
    <p
      className={cn(
        'mt-2 text-pretty text-4xl font-semibold tracking-tight text-white sm:text-5xl',
        className
      )}
      {...props}
    />
  );
};

const CaseStudyDescription: FC<
  Readonly<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, ...props }) => {
  return (
    <p className={cn('mt-6 text-lg/8 text-gray-300', className)} {...props} />
  );
};

const CaseStudyActions: FC<CaseStudyActionsProps> = ({
  className,
  actions,
  ...props
}) => {
  return (
    <div className={cn('mt-10 gap-x-6', className)} {...props}>
      {actions.map((action, index) => (
        <Link
          key={index}
          href={action.href}
          className={cn(
            'text-sm/6 font-semibold text-[#F24E1E] underline-offset-4 hover:underline'
          )}
        >
          {action.text} <span aria-hidden="true">→</span>
        </Link>
      ))}
    </div>
  );
};

export {
  CaseStudyActions,
  CaseStudy,
  CaseStudyColumn,
  CaseStudyDescription,
  CaseStudyHeading,
  CaseStudySubHeading,
  CaseStudyHeader,
};
